/** Carte de crise : titre, texte, options avec résumé des conséquences (dérivé de l'option, pas d'un texte à part). */
import type { EventOption, GameEvent, MeasureEffects, Profile } from "../../../engine/src/types.js";
import type { UiEventOption, UiGameEvent } from "../content-types.js";
import { escapeHtml as e, num, signed } from "../format.js";
import { optionPol, polPill, polText } from "../political.js";
import { srcButton } from "./source-info.js";
import { pastille } from "./brand.js";
import { crisisArt, icon } from "../icons.js";

const lastOf = (v: Profile) => (Array.isArray(v) ? (v[v.length - 1] ?? 0) : v);

export function optionSummary(o: EventOption, missionLabel: (id: string) => string = (id) => id.replace(/_/g, " ")): string {
  const parts: string[] = [];
  if (Array.isArray(o.cost)) {
    const tot = o.cost.reduce((a, b) => a + b, 0);
    if (Math.abs(tot) >= 0.05) parts.push(`${tot > 0 ? "dette" : "économies"} ${num(Math.abs(tot), 1)} Md€${o.cost.length > 1 ? ` sur ${o.cost.length} ans` : ""}`);
  } else if (Math.abs(o.cost) >= 0.05) parts.push(`${o.cost > 0 ? "dette" : "économies"} ${num(Math.abs(o.cost), 1)} Md€`);
  const f = o.fx;
  if (f.conf !== undefined) parts.push(`confiance ${signed(lastOf(f.conf), 0)}`);
  if (f.eu !== undefined) parts.push(`UE ${signed(lastOf(f.eu), 0)}`);
  if (f.coh !== undefined) parts.push(`cohésion ${signed(lastOf(f.coh), 0)}`);
  if (f.u !== undefined) parts.push(`chômage ${signed(lastOf(f.u), 1)} pt`);
  if (f.pov !== undefined) parts.push(`pauvreté ${signed(lastOf(f.pov), 1)} pt`);
  if (f.g !== undefined) parts.push(`croissance ${signed(lastOf(f.g), 2)} pt`);
  if (f.lab !== undefined) parts.push(`population active ${lastOf(f.lab) > 0 ? "+" : "−"}`);
  if (f.trade !== undefined) parts.push(`commerce ext. ${signed(lastOf(f.trade), 0)} Md€`);
  if (f.svc) for (const [id, v] of Object.entries(f.svc)) parts.push(`${missionLabel(id)} ${signed(lastOf(v), 2)}`);
  if (o.political_cost) parts.push(polText(optionPol(o, total(o.cost))));
  return parts.join(" · ") || "aucun effet budgétaire direct";
}

const fmtProfile = (v: Profile, dec: number) => (Array.isArray(v) ? `[${v.map((x) => signed(x, dec)).join(", ")}] (année par année)` : signed(v, dec));

/** Détail « Pourquoi ? » d'une option : coût et canaux bruts, note de l'option, note et sources de l'événement. */
export function optionWhy(ev: GameEvent, o: EventOption, missionLabel: (id: string) => string = (id) => id.replace(/_/g, " ")): string {
  const rows: string[] = [];
  rows.push(`<li>Coût budgétaire (Md€, dette) : ${fmtProfile(o.cost, 1)}</li>`);
  rows.push(`<li>${e(polText(optionPol(o, total(o.cost))))} (immédiat, au moment du choix)</li>`);
  const names: Record<string, string> = { g: "croissance (pt)", u: "chômage (pt)", pov: "pauvreté (pt)", coh: "cohésion", conf: "confiance", eu: "relation UE", co2: "CO₂ (%/an)", lab: "population active (%/an)", trade: "commerce extérieur (Md€)", asso: "solde Sécu (Md€)", apul: "solde collectivités (Md€)", infl: "inflation (pt)", oat: "OAT 10 ans (pt)", debt_share: "stock de dette (%)" };
  for (const [k, v] of Object.entries(o.fx)) {
    if (k === "svc") {
      for (const [mid, p] of Object.entries(v as Record<string, Profile>)) rows.push(`<li>Indice de service ${e(missionLabel(mid))} : ${fmtProfile(p, 2)}</li>`);
    } else if (v !== undefined) rows.push(`<li>${e(names[k] ?? k)} : ${fmtProfile(v as Profile, 2)}</li>`);
  }
  const srcs = ev.sources.length
    ? `<p class="sm">Sources : ${ev.sources.map((s) => `<a href="${e(s.url)}" target="_blank" rel="noopener noreferrer">${e(s.publisher)} — ${e(s.title)}</a>`).join(" · ")}</p>`
    : `<p class="sm mut">Aucune source : scénario de jeu (prototype), effets = hypothèses de modélisation non sourcées.</p>`;
  // Notes de modélisation (identifiants, renvois d'ADR) : jamais sur la carte, seulement dans ⓘ (red team v0.2 m3).
  const on = o.note && !isTechnicalNote(o.note) ? `<p class="sm">${e(o.note)}</p>` : "";
  const en = ev.note && !isTechnicalNote(ev.note) ? `<p class="sm mut">${e(ev.note)}</p>` : "";
  return `<div class="why"><ul class="sm">${rows.join("")}</ul>${on}${en}${srcs}<p class="hint">Effets appliqués à partir de l'année suivante (scalaire = une année, tableau = année par année).</p></div>`;
}

/** Note destinée aux développeurs (renvoi d'ADR, identifiant de code ou de paramètre) : pas affichée sur la carte. */
export function isTechnicalNote(note: string): boolean {
  return /\bADR\s*\d|\b[a-z]+_[a-z0-9]+(?:_[a-z0-9]+)*\b|\bfx\.|Convention \(moteur/u.test(note);
}

/** Effets d'un choc d'événement (subis quelle que soit la réponse), en clair. */
export function shockSummary(fx: MeasureEffects): string {
  const parts: string[] = [];
  if (fx.g !== undefined) parts.push(`croissance ${signed(lastOf(fx.g), 1)} pt`);
  if (fx.infl !== undefined) parts.push(`inflation ${signed(lastOf(fx.infl), 1)} pt`);
  if (fx.oat !== undefined) parts.push(`taux d'emprunt ${signed(lastOf(fx.oat), 1)} pt`);
  if (fx.u !== undefined) parts.push(`chômage ${signed(lastOf(fx.u), 1)} pt`);
  if (fx.trade !== undefined) parts.push(`commerce extérieur ${signed(lastOf(fx.trade), 0)} Md€`);
  if (fx.coh !== undefined) parts.push(`cohésion ${signed(lastOf(fx.coh), 0)}`);
  if (fx.conf !== undefined) parts.push(`confiance des marchés ${signed(lastOf(fx.conf), 0)}`);
  return parts.join(" · ");
}

const total = (c: Profile) => (Array.isArray(c) ? c.reduce((a, b) => a + b, 0) : c);

/** Montant d'une option sur le solde (« −12,3 Md€ », « +4,0 Md€ », « 0 Md€ ») : à l'encre, jamais coloré (ADR 0018). */
export function optionAmount(o: UiEventOption): string {
  const tot = total(o.cost);
  if (Math.abs(tot) < 0.05) return "0 Md€";
  return `${tot > 0 ? "−" : "+"}${num(Math.abs(tot), 1)} Md€`;
}

/** Coût et financement d'une option, en format « mini-financement » (barre recettes / dette, neutre). */
export function optionBudget(o: UiEventOption): string {
  const tot = total(o.cost);
  const years = Array.isArray(o.cost) ? o.cost.filter((x) => x !== 0).length : tot !== 0 ? 1 : 0;
  if (Math.abs(tot) < 0.05) return `<div class="ob"><span class="mut">Pas de dépense directe.</span></div>`;
  if (tot < 0) return `<div class="ob"><span class="mut">${num(-tot, 1)} Md€ d'économies${years > 1 ? ` sur ${years} ans` : ""} : réduit le déficit.</span></div>`;
  const f = o.financing;
  const rev = Math.max(0, Math.min(tot, f?.revenue_bn ?? 0));
  const debt = f?.debt_bn !== undefined ? Math.max(0, f.debt_bn) : tot - rev;
  const pRev = (rev / Math.max(tot, 1e-9)) * 100;
  return `<div class="ob"><span class="mut">Coûte ${num(tot, 1)} Md€ ${years > 1 ? `sur ${years} ans` : "une fois"}.</span>
    <div class="bar" role="img" aria-label="Financé à ${num(pRev, 0)} % par des recettes, le reste par la dette"><i class="rev" style="width:${pRev.toFixed(1)}%"></i><i class="debt" style="width:${(100 - pRev).toFixed(1)}%"></i></div>
    <span class="xs">${rev < 0.05 ? `<i class="sw debt" aria-hidden="true"></i>financé à 100 % par la dette` : `<i class="sw rev" aria-hidden="true"></i>recettes ${num(rev, 1)} Md€ · <i class="sw debt" aria-hidden="true"></i>dette ${num(debt, 1)} Md€`}${f?.note ? ` · ${e(f.note)}` : ""}</span></div>`;
}

export interface OptionOutcome {
  /** KPIs de la dernière année simulée avec cette option. */
  kpis: Record<string, number>;
  year: number | undefined;
  gameOver: string | null;
}

/** Indicateurs de l'aperçu d'option : clé, libellé, sens favorable, format, décimales de l'écart. */
export const OUTCOME_KEYS: Array<{ key: string; label: string; better: 1 | -1; fmt: (v: number) => string; dfmt: (d: number) => string }> = [
  { key: "deficit_pct_gdp", label: "Déficit", better: 1, fmt: (v) => `${num(Math.abs(v), 1)} %`, dfmt: (d) => `${signed(-d, 1)} pt` },
  { key: "debt_pct_gdp", label: "Dette", better: -1, fmt: (v) => `${num(v, 1)} %`, dfmt: (d) => `${signed(d, 1)} pt` },
  { key: "purchasing_power_index", label: "Pouvoir d'achat", better: 1, fmt: (v) => num(v, 1), dfmt: (d) => `${signed(d, 1)} pt` },
  { key: "parliament_majority", label: "Majorité", better: 1, fmt: (v) => `${num(v, 0)} %`, dfmt: (d) => `${signed(d, 0)} pt` },
];

/**
 * Options dans l'ordre d'affichage : par coût budgétaire total croissant (économies d'abord, dépenses les plus lourdes à
 * la fin), puis coût politique, puis ordre du contenu. Même règle pour toutes les crises : aucune option n'est mise en
 * avant par préférence éditoriale (ADR 0011, red team v0.2 M2).
 */
export function sortedOptions<T extends EventOption>(options: T[]): T[] {
  return options
    .map((o, i) => ({ o, i }))
    .sort((a, b) => total(a.o.cost) - total(b.o.cost) || a.o.political_cost - b.o.political_cost || a.i - b.i)
    .map((x) => x.o);
}

/**
 * Réponse de référence : `reference_option_id` de l'événement (v0.3) ; à défaut la « réponse minimale » = coût budgétaire
 * le plus proche de zéro, puis coût politique le plus faible en valeur absolue, puis ordre du contenu.
 */
export function referenceOption(ev: UiGameEvent): UiEventOption | undefined {
  const declared = ev.reference_option_id ? ev.options.find((o) => o.id === ev.reference_option_id) : undefined;
  if (declared) return declared;
  return ev.options
    .map((o, i) => ({ o, i }))
    .sort((a, b) => Math.abs(total(a.o.cost)) - Math.abs(total(b.o.cost)) || Math.abs(a.o.political_cost) - Math.abs(b.o.political_cost) || a.i - b.i)[0]?.o;
}

/** Aperçu d'une option : fin de mandat, et écart à la réponse de référence (▲ mieux / ▼ moins bien pour le pays). */
export function outcomesRow(o: OptionOutcome, ref: OptionOutcome | undefined, isRef: boolean): string {
  if (o.gameOver) return `<p class="xs down">Mandat interrompu : ${e(o.gameOver)}</p>`;
  const cells = OUTCOME_KEYS.filter((k) => o.kpis[k.key] !== undefined).map((k) => {
    const v = o.kpis[k.key]!;
    const r = ref && !ref.gameOver ? ref.kpis[k.key] : undefined;
    let delta = "";
    if (!isRef && r !== undefined) {
      const d = v - r;
      const shown = k.dfmt(d);
      const zero = /^[+−]?0(,0)?\s?pt$/u.test(shown);
      const effect = zero ? "equal" : d * k.better > 0 ? "better" : "worse";
      // Flèche : signe de l'écart affiché ; couleur : effet sur le pays dans le sens propre de l'indicateur.
      delta = pastille(effect, zero ? 0 : shown.startsWith("−") ? -1 : 1, zero ? "égal" : shown, "face à la réponse minimale");
    }
    return `<div><span class="l">${k.label}</span><b>${k.fmt(v)}</b>${delta}</div>`;
  });
  return `<div class="outc">${cells.join("")}</div>`;
}

/**
 * Carte de crise (IDENTITE.md §7) : illustration quand l'identité en fournit une pour cet événement (icons.ts), titre en Archivo
 * capitales, ligne « crise · décision · année » sous le titre (pas de sur-titre au-dessus), une phrase, puis les réponses :
 * boutons secondaires pleine largeur, montant à droite.
 */
export function decisionCard(
  ev: UiGameEvent,
  year: number,
  meta: { index: number; total: number; endYear: number | undefined },
  outcomes: Record<string, OptionOutcome>,
  missionLabel?: (id: string) => string,
): string {
  const tech = [ev.note, ...ev.options.map((o) => o.note)].filter((n): n is string => !!n && isTechnicalNote(n));
  const src = srcButton({ title: ev.title, sources: ev.sources.length ? ev.sources : ["TODO"], status: ev.sources.length ? undefined : "non_chiffré", notes: [...(ev.note && !isTechnicalNote(ev.note) ? [ev.note] : []), ...tech.map((n) => `Note de modélisation : ${n}`)] }, { inline: true });
  const shock = ev.shock ? shockSummary(ev.shock) : "";
  const ref = referenceOption(ev);
  const refOut = ref ? outcomes[ref.id] : undefined;
  const opts = sortedOptions(ev.options);
  const art = crisisArt(ev.id);
  return `<div class="card event" data-event="${ev.id}">
    ${art ? `<img class="crisis-art" src="${art}" alt="" width="320" height="200">` : ""}
    <h2 class="crisis-title">${e(ev.title)}${src}</h2>
    <p class="crisis-meta">${icon("crise", 20)}<span>Crise · décision ${meta.index} sur ${meta.total} · ${year}</span></p>
    <p>${e(ev.text)}</p>
    ${shock ? `<div class="shock"><p class="sm"><b>Quoi que tu fasses :</b> ${e(shock)}</p><p class="xs mut">Ce choc arrive quelle que soit ta réponse : il n'est pas compté comme un effet de ta décision.</p></div>` : ""}
    <p class="hint">Chaque réponse est un petit budget : ce qu'elle coûte, son coût politique, et où elle mène le pays en ${meta.endYear ?? "fin de mandat"}. Les écarts sont calculés par rapport à la réponse minimale${ref ? ` (« ${e(ref.label)} »)` : ""}. Réponses rangées de la moins chère à la plus chère.</p>
    ${opts
      .map((o) => {
        const out = outcomes[o.id];
        const isRef = ref?.id === o.id;
        return `<div class="opt${isRef ? " ref" : ""}">
          <div class="ot">${e(o.label)}${isRef ? ` <span class="badge mut">réponse minimale (référence)</span>` : ""}</div>${optionBudget(o)}<p class="xs">${polPill(optionPol(o, total(o.cost)), { ratio: true })}</p>${o.shock ? `<p class="xs">Effet propre à cette réponse : ${e(shockSummary(o.shock))}</p>` : ""}${o.risk ? `<p class="xs warn">Risque : ${e(o.risk.label)} (${num(o.risk.probability * 100, 0)} % de chances${o.risk.political_cost ? `, ${e(polText({ points: o.risk.political_cost, perYear: false }))} en plus` : ""})</p>` : ""}${out ? `${isRef ? "" : `<p class="xs mut">par rapport à la réponse minimale :</p>`}${outcomesRow(out, refOut, isRef)}` : ""}
          <details class="more why-d"><summary>Détail des effets</summary><p class="xs mut">${e(optionSummary(o, missionLabel))}</p>${optionWhy(ev as GameEvent, o, missionLabel)}</details>
          <button type="button" class="choice" data-choice="${o.id}" aria-label="Choisir : ${e(o.label)} (${e(optionAmount(o))})"><span>Choisir cette réponse</span><span class="amt">${optionAmount(o)}</span></button></div>`;
      })
      .join("")}
    <div class="opt free"><div class="ot">Proposer ma réponse</div><p class="hint">Bientôt : ta réponse libre, chiffrée en direct.</p><button type="button" class="choice sec" disabled aria-disabled="true">Proposer ma réponse</button></div>
  </div>`;
}
